import { useState, useEffect, useCallback } from 'react';
import Skeleton from 'react-loading-skeleton';
import { ChevronLeftIcon } from '@heroicons/react/24/outline';

import Typography from '~/shared/components/Typography';
import Loader from '~/shared/components/Loader';
import Button from '~/shared/components/ButtonsV2';
import RevertAllModal from '~/settings/components/ConfigurationsPage/ConfigurationDetails/RevertAllModal';
import Tag from '~/shared/components/Tag';
import { usePermission } from '~/shared/hooks/usePermission';

import { useConfigurationQuery } from '../hooks/useConfigurationQuery';
import { formatConfigurationLevelLabel, isInstanceFamilyLevel } from '../helpers/utils';

import { useConfigurationForm } from './hooks/useConfigurationForm';
import {
  buildApiPayload,
  buildFlattenedData,
  buildOriginalValues,
  buildSavedFields,
  createDisplayField,
} from './helpers/formHelpers';
import ConfigurationFooter from './ConfigurationFooter';
import ConfigurationField from './ConfigurationField';

import type { RevertConfigurationResponse } from '~/settings/components/ConfigurationsPage/helpers/configurationApi';
import type { ConfigTreeNode } from '~/settings/components/ConfigurationsPage/types';

interface NodeData {
  id: string | null;
  name: string | null;
  type: ConfigTreeNode['type'] | null;
  level?: ConfigTreeNode['level'];
  clusterId?: string;
  instanceId?: string;
  region?: string;
  accountId?: string;
}

interface ConfigurationDetailsProps {
  nodeData: NodeData;
  onBack?: () => void;
  onCancel?: () => void;
  isAddNewConfigurationModal?: boolean;
  selectNode?: (nodeId: string | null) => void;
  onSave: (
    nodeId: string,
    fields: Partial<Record<string, string | number>>,
    originalValues: Record<string, string>
  ) => Promise<void>;
  onRevertField: (nodeId: string, fieldName: string) => Promise<RevertConfigurationResponse>;
  onRevertAll: (nodeId: string) => Promise<RevertConfigurationResponse>;
  onHasChangesChange?: (hasChanges: boolean) => void;
  onSaveFormReady?: (saveFn: () => Promise<void>) => void;
}

export default function ConfigurationDetails({
  nodeData,
  onBack,
  // onCancel,
  isAddNewConfigurationModal,
  // selectNode,
  onSave,
  onRevertField,
  onRevertAll,
  onHasChangesChange,
  onSaveFormReady,
}: ConfigurationDetailsProps) {
  const canWriteConfig = usePermission('datafy.write.config');
  const {
    id: nodeId,
    name: nodeName,
    type: nodeType,
    level: nodeLevel,
    clusterId,
    instanceId,
    region,
    accountId,
  } = nodeData;
  const effectiveInstanceFamilyId =
    nodeLevel === 'node' || nodeLevel === 'volume' ? (nodeId ?? instanceId ?? undefined) : instanceId;

  const [isSaving, setIsSaving] = useState(false);
  const [showRevertAllModal, setShowRevertAllModal] = useState(false);

  const { data: configRecord, isLoading: loading } = useConfigurationQuery({
    nodeId,
    nodeName,
    nodeType,
    level: nodeLevel,
    clusterId,
    instanceId: effectiveInstanceFamilyId,
    region,
    accountId,
  });

  const {
    register,
    control,
    fields,
    errors,
    isValid,
    hasChanges,
    revertingField,
    isRevertingAll,
    handleSubmit,
    getValues,
    setError,
    reset,
    handleRevertField,
    handleRevertAll,
  } = useConfigurationForm({
    nodeId,
    nodeLevel,
    configRecord: configRecord ?? undefined,
    clusterId,
    instanceId: effectiveInstanceFamilyId,
    region,
    accountId,
    onHasChangesChange,
    onRevertField,
    onRevertAll,
  });

  const handleRevertAllClick = () => {
    setShowRevertAllModal(true);
  };

  const handleRevertAllConfirm = async () => {
    setShowRevertAllModal(false);
    await handleRevertAll();
  };

  // Extract save logic into a reusable async function
  const performSave = useCallback(async () => {
    if (!nodeId || !configRecord || !nodeLevel) {
      return;
    }

    // Validate required parameters based on level
    if (isInstanceFamilyLevel(nodeLevel) && !effectiveInstanceFamilyId) {
      console.error('id is required for node/volume and instanceId is required for instance level');
      return;
    }

    // Check if form is valid before saving
    if (!isValid) {
      throw new Error('Form validation failed');
    }

    setIsSaving(true);
    try {
      const originalValues = buildOriginalValues(configRecord);
      const flattenedData = buildFlattenedData(configRecord, getValues);
      const apiPayload = buildApiPayload(flattenedData, configRecord);

      await onSave(nodeId, apiPayload, originalValues);

      const savedFields = buildSavedFields(configRecord, flattenedData);
      reset(savedFields);
      onHasChangesChange?.(false);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { errors?: Record<string, string> } } };
      const fieldErrors = err?.response?.data?.errors;
      if (fieldErrors && typeof fieldErrors === 'object') {
        Object.entries(fieldErrors).forEach(([fieldName, message]) => {
          setError(fieldName, { type: 'server', message });
        });
      }
      console.error('Failed to save:', error);
      throw error;
    } finally {
      setIsSaving(false);
    }
  }, [
    nodeId,
    configRecord,
    nodeLevel,
    effectiveInstanceFamilyId,
    isValid,
    getValues,
    setError,
    onSave,
    reset,
    onHasChangesChange,
  ]);

  const handleSave = handleSubmit(
    async (_data) => {
      await performSave();
    },
    (errors) => {
      // Handle validation errors
      console.error('[ConfigurationDetails] Form validation failed:', errors);
    }
  );

  const handleCancel = () => {
    if (!configRecord) {
      return;
    }

    const originalFields = buildOriginalValues(configRecord);
    reset(originalFields);
    onHasChangesChange?.(false);

    // if (isAddNewConfigurationModal) {
    //   onCancel?.();
    // }
  };

  // Register the save function with parent component
  useEffect(() => {
    if (onSaveFormReady) {
      onSaveFormReady(performSave);
    }
  }, [performSave, onSaveFormReady]);

  if (!nodeId) {
    return (
      <div className="flex items-center justify-center h-full">
        <Typography variant="p2" className="text-primary-900 opacity-50">
          Select a configuration item to view details
        </Typography>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-1 justify-center items-center h-full min-h-[calc(100vh-120px)] 2xl:min-h-[calc(100vh-240px)] overflow-y-auto">
        <Loader variant="xl" />
      </div>
    );
  }

  if (!configRecord) {
    return (
      <div className="flex items-center justify-center h-full">
        <Typography variant="p2" className="text-primary-900">
          Configuration not found
        </Typography>
      </div>
    );
  }

  // Calculate hasOverrides - check if any field is overridden (matching individual revert button logic)
  const hasOverrides = configRecord.fields.some((field) => {
    return field.isOverridden === true;
  });

  return (
    <div className="flex flex-col h-full bg-white overflow-hidden">
      <div
        className={`"flex justify-between items-center px-5  ${isAddNewConfigurationModal ? 'pb-2' : 'py-[30px]'} flex-shrink-0"`}
      >
        <div className="flex gap-3 items-center">
          {onBack && (
            <Button
              className="w-10"
              variant="third"
              icon={<ChevronLeftIcon className="size-5" />}
              onClick={onBack}
            />
          )}
          {nodeName ? (
            <Typography variant="H4-med" fontWeight={500} className="text-primary-900">
              {nodeName}
            </Typography>
          ) : (
            <Skeleton
              containerClassName=" block leading-[unset!important]"
              className="h-[27px] leading-[unset!important] rounded-[8px] "
              baseColor="var(--color-gray-200)"
              highlightColor="#FFFF"
              width={80}
            />
          )}
          <Tag
            text={formatConfigurationLevelLabel(nodeLevel!)}
            variant={nodeLevel}
            className="capitalize"
          />
        </div>
      </div>

      <div
        className={`"flex-1 min-h-0 px-5 overflow-y-auto ${isAddNewConfigurationModal ? 'side-panel-scrollbar mx-6' : ''}`}
        style={{ direction: isAddNewConfigurationModal ? 'rtl' : 'ltr' }}
      >
        <div style={{ direction: isAddNewConfigurationModal ? 'ltr' : 'ltr' }}>
          {configRecord.fields.map((field) => {
            const displayField = createDisplayField(field, fields);

            return (
              <ConfigurationField
                key={field.name}
                field={displayField}
                onChange={() => { }}
                onRevert={handleRevertField}
                register={register}
                control={control}
                errors={errors}
                isReverting={revertingField === field.name}
                canWriteConfig={canWriteConfig}
              />
            );
          })}
        </div>
      </div>

      {canWriteConfig && (
        <div className="flex-shrink-0">
          <ConfigurationFooter
            lastUpdated={configRecord.lastUpdated}
            hasChanges={hasChanges}
            hasOverrides={hasOverrides}
            isFormValid={isValid}
            onCancel={handleCancel}
            onSave={handleSave}
            onRevertAll={handleRevertAllClick}
            isSaving={isSaving}
            isRevertingAll={isRevertingAll}
          />
        </div>
      )}

      <RevertAllModal
        isOpen={showRevertAllModal}
        onClose={() => setShowRevertAllModal(false)}
        onConfirm={handleRevertAllConfirm}
      />
    </div>
  );
}
